import { Auth } from 'aws-amplify';
import { dialogService } from './dialog.service';

/**
 * Retrieves the current Cognito ID token.
 *
 * @returns {Promise<string>} - A promise that resolves to the current ID token as a string.
 * @throws {Error} - Throws an error if the current authenticated user cannot be retrieved.
 */
export async function getCognitoIdToken() {
  const result = await Auth.currentAuthenticatedUser();
  return result.signInUserSession.idToken.jwtToken;
}

/**
 * Retrieves the current Cognito access token.
 *
 * @returns {Promise<string>} - A promise that resolves to the current access token as a string.
 * @throws {Error} - Throws an error if the current authenticated user cannot be retrieved.
 */
export async function getCognitoAccessToken() {
  const result = await Auth.currentAuthenticatedUser();
  return result.signInUserSession.accessToken.jwtToken;
}

/**
 * Retrieves the expiration time of the current Cognito access token.
 *
 * @returns {Promise<number>} - A promise that resolves to the expiration time (in seconds since the epoch) of the access token.
 * @throws {Error} - Throws an error if the current authenticated user cannot be retrieved.
 */
export async function getCognitoAccessTokenExp() {
  const result = await Auth.currentAuthenticatedUser();
  return result.signInUserSession.accessToken.payload.exp;
}

/**
 * Checks if the Cognito access token is expired.
 *
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating whether the token is expired.
 */
export async function isCognitoAccessTokenExpired() {
  try {
    const expirationTime = (await getCognitoAccessTokenExp()) * 1000;
    const currentTime = Date.now();
    return expirationTime < currentTime;
  } catch (error) {
    console.error('Failed to check token expiration: ', error);
    return true;
  }
}

/**
 * Refreshes the current Cognito session and retrieves a new set of tokens.
 *
 * @returns {Promise<Object>} - A promise that resolves to an object containing the new access, ID, and refresh tokens.
 * @throws {Error} - Throws an error if the current authenticated user cannot be retrieved or the session cannot be refreshed.
 */
export async function refreshTokenCognito() {
  return new Promise((resolve) => {
    Auth.currentSession()
      .then((session) => {
        var refreshToken = session.getRefreshToken();
        Auth.currentAuthenticatedUser()
          .then((res) => {
            res.refreshSession(refreshToken, (err, data) => {
              if (err) {
                console.error('Faild to refresh the token ', err);
                resolve({ error: err });
              } else {
                resolve({
                  token: data.getIdToken().getJwtToken(),
                });
              }
            });
          })
          .catch((error) => {
            console.error('Faild to get current authenticated user ', error);
            resolve({ token: null, error });
          });
      })
      .catch((error) => {
        console.error('Faild to get current session ', error);
        // No logged-in user: don't set auth header
        resolve({ token: null, error });
      });
  });
}

/**
 * Changes the password for the current authenticated Cognito user.
 *
 * @param {string} oldPassword - The current password of the user.
 * @param {string} newPassword - The new password to be set for the user.
 * @returns {Promise<void>} - A promise that resolves when the password has been successfully changed.
 * @throws {Error} - Throws an error if the current authenticated user cannot be retrieved or the password change fails.
 */
export async function changePasswordCognito(oldPassword, newPassword) {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const result = await Auth.changePassword(user, oldPassword, newPassword);
    return result;
  } catch (error) {
    if (error.code === 'NotAuthorizedException') {
      error.code = 'NotAuthorizedException.ChangePassword';
    }
    handleCognitoError(error);
    throw error;
  }
}

export async function isCognitoSessionValid() {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    if (!user) {
      const error = new Error('No authenticated user found');
      error.code = 'NotAuthorizedException';
      return { valid: false, error };
    }

    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken();
    const now = Date.now() / 1000;

    if (accessToken.getExpiration() < now) {
      const error = new Error('Access token expired');
      error.code = 'TokenExpired';
      return { valid: false, error };
    }

    return { valid: true };
  } catch (err) {
    console.warn('[WARN] Cognito session validation failed:', err);
    return { valid: false, error: err };
  }
}

/**
 * Handles errors returned by AWS Cognito and provides user-friendly error messages.
 *
 * @param {Error} error - The error object returned by AWS Cognito.
 */
export function handleCognitoError(error) {
  dialogService.cognitoError({ error });
}
